import React, { createContext, useContext, useState, useEffect } from 'react';

const LanguageContext = createContext();

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within LanguageProvider');
  }
  return context;
};

// Translation dictionaries
const translations = {
  en: {
    // Navigation
    'nav.overview': 'Overview',
    'nav.geographic': 'Geographic View',
    'nav.network': 'Network Map',
    'nav.flow': 'Flow Hierarchy',
    'nav.nrw': 'NRW Analysis',
    'nav.quality': 'Water Quality',
    'nav.alerts': 'Alerts',
    'nav.smartAnalytics': 'Smart Analytics',
    'nav.historical': 'Historical Data',
    'nav.reports': 'Reports & Export',
    'nav.settings': 'Settings',
    
    // Header
    'header.lastUpdated': 'Last updated',
    'header.location': 'Salcette Block',
    'header.region': 'Goa, India',
    
    // Auth
    'auth.welcome': 'Welcome Back',
    'auth.enterCredentials': 'Enter your credentials to access the dashboard',
    'auth.userId': 'User ID',
    'auth.password': 'Password',
    'auth.signIn': 'Sign In',
    'auth.signingIn': 'Signing in...',
    'auth.authorizedOnly': 'Authorized personnel only. For assistance, contact your system administrator.',
    'auth.loginFailed': 'Login failed. Please check your credentials.',
    'auth.loggedInAs': 'Logged in as',
    'auth.logout': 'Logout',
    
    // Overview
    'overview.title': 'Village Overview',
    'overview.subtitle': 'Real-time monitoring of water supply infrastructure for Salcette Block, Goa',
    'overview.totalWtps': 'Total WTPs',
    'overview.underMaintenance': 'under maintenance',
    'overview.villagesCovered': 'Villages Covered',
    'overview.totalHouseholds': 'Total Households',
    'overview.lpcdAvg': 'LPCD avg',
    'overview.target': 'Target',
    'overview.villageNrw': 'Village NRW',
    'overview.supplyDemand': 'Supply vs Demand Trend (MLD)',
    'overview.nrwDistribution': 'NRW Loss Distribution by Village',
    'overview.villageSummary': 'Village-wise Infrastructure Summary',
    
    // Smart Analytics
    'analytics.title': 'Smart Analytics',
    'analytics.subtitle': 'AI-powered anomaly detection and predictive analytics for water supply monitoring',
    'analytics.healthScore': 'System Health Score',
    'analytics.nrwScore': 'NRW Score',
    'analytics.quality': 'Quality',
    'analytics.flowEfficiency': 'Flow Efficiency',
    'analytics.anomalyDetection': 'AI Anomaly Detection',
    'analytics.anomalyDesc': 'Detect unusual patterns in water flow, NRW spikes, and quality issues',
    'analytics.runAnalysis': 'Run AI Analysis',
    'analytics.analyzing': 'Analyzing...',
    'analytics.predictions': 'Predictive Analytics',
    'analytics.predictionsDesc': 'AI-powered forecasting for water supply metrics',
    'analytics.generateForecast': 'Generate Forecast',
    'analytics.predicting': 'Predicting...',
    'analytics.anomaliesDetected': 'Anomalies Detected',
    'analytics.highSeverity': 'High Severity',
    'analytics.mediumSeverity': 'Medium Severity',
    'analytics.recommendations': 'Recommendations',
    'analytics.aiRecommendations': 'AI Recommendations',
    
    // Reports
    'reports.title': 'Reports & Export',
    'reports.subtitle': 'Download comprehensive reports in various formats for analysis and record-keeping',
    'reports.exportExcel': 'Export to Excel',
    'reports.exportDesc': 'Download dashboard data in Excel format for offline analysis and reporting',
    'reports.fullReport': 'Full Report',
    'reports.fullReportDesc': 'Complete dashboard data with all sections',
    'reports.overviewReport': 'Overview',
    'reports.overviewDesc': 'Summary metrics and KPIs',
    'reports.villagesReport': 'Villages',
    'reports.villagesDesc': 'Village-wise infrastructure data',
    'reports.nrwReport': 'NRW Analysis',
    'reports.nrwDesc': 'Non-Revenue Water breakdown',
    'reports.flowReport': 'Flow Hierarchy',
    'reports.flowDesc': 'WTP to Household flow data',
    'reports.qualityReport': 'Water Quality',
    'reports.qualityDesc': 'Water quality test results',
    'reports.download': 'Download',
    'reports.generating': 'Generating Excel...',
    'reports.recentExports': 'Recent Exports',
    
    // Real-time
    'realtime.nextRefresh': 'Next',
    'realtime.autoRefreshOff': 'Auto-refresh off',
    'realtime.notifications': 'Notifications',
    'realtime.clearAll': 'Clear all',
    'realtime.noNotifications': 'No notifications',
    'realtime.settings': 'Real-time Settings',
    'realtime.autoRefresh': 'Auto-refresh',
    'realtime.refreshInterval': 'Refresh Interval',
    'realtime.lastUpdated': 'Last updated',
    
    // Common
    'common.loading': 'Loading...',
    'common.error': 'Error',
    'common.success': 'Success',
    'common.save': 'Save',
    'common.cancel': 'Cancel',
    'common.refresh': 'Refresh',
    'common.active': 'Active',
    'common.good': 'Good',
    'common.warning': 'Warning',
    'common.critical': 'Critical',
    'common.village': 'Village',
    'common.households': 'Households',
    'common.status': 'Status',
    
    // Table headers
    'table.village': 'Village',
    'table.wtps': 'WTPs',
    'table.ohts': 'OHTs',
    'table.dmas': 'DMAs',
    'table.households': 'Households',
    'table.nrw': 'NRW %',
    'table.alerts': 'Alerts',
    'table.status': 'Status',
  },
  
  hi: {
    // Navigation
    'nav.overview': 'अवलोकन',
    'nav.geographic': 'भौगोलिक दृश्य',
    'nav.network': 'नेटवर्क मानचित्र',
    'nav.flow': 'प्रवाह पदानुक्रम',
    'nav.nrw': 'NRW विश्लेषण',
    'nav.quality': 'जल गुणवत्ता',
    'nav.alerts': 'अलर्ट',
    'nav.smartAnalytics': 'स्मार्ट एनालिटिक्स',
    'nav.historical': 'ऐतिहासिक डेटा',
    'nav.reports': 'रिपोर्ट और निर्यात',
    'nav.settings': 'सेटिंग्स',
    
    // Header
    'header.lastUpdated': 'अंतिम अपडेट',
    'header.location': 'सालसेट ब्लॉक',
    'header.region': 'गोवा, भारत',
    
    // Auth
    'auth.welcome': 'पुनः स्वागत है',
    'auth.enterCredentials': 'डैशबोर्ड तक पहुंचने के लिए अपनी साख दर्ज करें',
    'auth.userId': 'यूजर आईडी',
    'auth.password': 'पासवर्ड',
    'auth.signIn': 'साइन इन करें',
    'auth.signingIn': 'साइन इन हो रहा है...',
    'auth.authorizedOnly': 'केवल अधिकृत कर्मचारी। सहायता के लिए, अपने सिस्टम व्यवस्थापक से संपर्क करें।',
    'auth.loginFailed': 'लॉगिन विफल। कृपया अपनी साख जांचें।',
    'auth.loggedInAs': 'लॉग इन है',
    'auth.logout': 'लॉग आउट',
    
    // Overview
    'overview.title': 'गांव अवलोकन',
    'overview.subtitle': 'सालसेट ब्लॉक, गोवा के लिए जल आपूर्ति बुनियादी ढांचे की वास्तविक समय निगरानी',
    'overview.totalWtps': 'कुल WTPs',
    'overview.underMaintenance': 'रखरखाव में',
    'overview.villagesCovered': 'कवर किए गए गांव',
    'overview.totalHouseholds': 'कुल घर',
    'overview.lpcdAvg': 'LPCD औसत',
    'overview.target': 'लक्ष्य',
    'overview.villageNrw': 'गांव NRW',
    'overview.supplyDemand': 'आपूर्ति बनाम मांग प्रवृत्ति (MLD)',
    'overview.nrwDistribution': 'गांव द्वारा NRW हानि वितरण',
    'overview.villageSummary': 'गांव-वार बुनियादी ढांचा सारांश',
    
    // Smart Analytics
    'analytics.title': 'स्मार्ट एनालिटिक्स',
    'analytics.subtitle': 'जल आपूर्ति निगरानी के लिए AI-संचालित विसंगति पहचान और भविष्यवाणी विश्लेषण',
    'analytics.healthScore': 'सिस्टम स्वास्थ्य स्कोर',
    'analytics.nrwScore': 'NRW स्कोर',
    'analytics.quality': 'गुणवत्ता',
    'analytics.flowEfficiency': 'प्रवाह दक्षता',
    'analytics.anomalyDetection': 'AI विसंगति पहचान',
    'analytics.anomalyDesc': 'जल प्रवाह, NRW वृद्धि और गुणवत्ता मुद्दों में असामान्य पैटर्न का पता लगाएं',
    'analytics.runAnalysis': 'AI विश्लेषण चलाएं',
    'analytics.analyzing': 'विश्लेषण हो रहा है...',
    'analytics.predictions': 'भविष्यवाणी विश्लेषण',
    'analytics.predictionsDesc': 'जल आपूर्ति मेट्रिक्स के लिए AI-संचालित पूर्वानुमान',
    'analytics.generateForecast': 'पूर्वानुमान उत्पन्न करें',
    'analytics.predicting': 'भविष्यवाणी हो रही है...',
    'analytics.anomaliesDetected': 'पहचानी गई विसंगतियां',
    'analytics.highSeverity': 'उच्च गंभीरता',
    'analytics.mediumSeverity': 'मध्यम गंभीरता',
    'analytics.recommendations': 'अनुशंसाएं',
    'analytics.aiRecommendations': 'AI अनुशंसाएं',
    
    // Reports
    'reports.title': 'रिपोर्ट और निर्यात',
    'reports.subtitle': 'विश्लेषण और रिकॉर्ड-कीपिंग के लिए विभिन्न प्रारूपों में व्यापक रिपोर्ट डाउनलोड करें',
    'reports.exportExcel': 'Excel में निर्यात करें',
    'reports.exportDesc': 'ऑफ़लाइन विश्लेषण और रिपोर्टिंग के लिए Excel प्रारूप में डैशबोर्ड डेटा डाउनलोड करें',
    'reports.fullReport': 'पूर्ण रिपोर्ट',
    'reports.fullReportDesc': 'सभी अनुभागों के साथ संपूर्ण डैशबोर्ड डेटा',
    'reports.overviewReport': 'अवलोकन',
    'reports.overviewDesc': 'सारांश मेट्रिक्स और KPIs',
    'reports.villagesReport': 'गांव',
    'reports.villagesDesc': 'गांव-वार बुनियादी ढांचा डेटा',
    'reports.nrwReport': 'NRW विश्लेषण',
    'reports.nrwDesc': 'गैर-राजस्व जल विश्लेषण',
    'reports.flowReport': 'प्रवाह पदानुक्रम',
    'reports.flowDesc': 'WTP से घर तक प्रवाह डेटा',
    'reports.qualityReport': 'जल गुणवत्ता',
    'reports.qualityDesc': 'जल गुणवत्ता परीक्षण परिणाम',
    'reports.download': 'डाउनलोड',
    'reports.generating': 'Excel जनरेट हो रहा है...',
    'reports.recentExports': 'हाल के निर्यात',
    
    // Real-time
    'realtime.nextRefresh': 'अगला',
    'realtime.autoRefreshOff': 'स्वतः-रिफ्रेश बंद',
    'realtime.notifications': 'सूचनाएं',
    'realtime.clearAll': 'सभी साफ़ करें',
    'realtime.noNotifications': 'कोई सूचना नहीं',
    'realtime.settings': 'रीयल-टाइम सेटिंग्स',
    'realtime.autoRefresh': 'स्वतः-रिफ्रेश',
    'realtime.refreshInterval': 'रिफ्रेश अंतराल',
    'realtime.lastUpdated': 'अंतिम अपडेट',
    
    // Common
    'common.loading': 'लोड हो रहा है...',
    'common.error': 'त्रुटि',
    'common.success': 'सफलता',
    'common.save': 'सहेजें',
    'common.cancel': 'रद्द करें',
    'common.refresh': 'रिफ्रेश',
    'common.active': 'सक्रिय',
    'common.good': 'अच्छा',
    'common.warning': 'चेतावनी',
    'common.critical': 'गंभीर',
    'common.village': 'गांव',
    'common.households': 'घर',
    'common.status': 'स्थिति',
    
    // Table headers
    'table.village': 'गांव',
    'table.wtps': 'WTPs',
    'table.ohts': 'OHTs',
    'table.dmas': 'DMAs',
    'table.households': 'घर',
    'table.nrw': 'NRW %',
    'table.alerts': 'अलर्ट',
    'table.status': 'स्थिति',
  }
};

const STORAGE_KEY = 'swati_language';

export const LanguageProvider = ({ children }) => {
  const [language, setLanguage] = useState(() => {
    const stored = localStorage.getItem(STORAGE_KEY);
    return stored || 'en';
  });

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, language);
    // Update document lang attribute
    document.documentElement.lang = language;
  }, [language]);

  const t = (key) => {
    return translations[language]?.[key] || translations['en']?.[key] || key;
  };

  const switchLanguage = (lang) => {
    if (translations[lang]) {
      setLanguage(lang);
    }
  };

  const availableLanguages = [
    { code: 'en', name: 'English', nativeName: 'English' },
    { code: 'hi', name: 'Hindi', nativeName: 'हिंदी' },
  ];

  const value = {
    language,
    setLanguage: switchLanguage,
    t,
    availableLanguages,
  };

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
};
